import React, { ReactNode } from 'react'

import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import SectionHeader from 'components_new/molecules/SectionHeader'

export interface KanbanColumnProps {
  actions?: ReactNode
  children: ReactNode
  count?: number
  // Pinned to the bottom of the column (e.g. a sum/total row).
  footer?: ReactNode
  icon?: IconProps
  id: string
  isActive?: boolean
  itemIds: string[]
  // When true a lock icon is shown in the header (where the add button sits).
  locked?: boolean
  lockedTooltip?: string
  onAddItem?: () => void
  sx?: SxProps
  title: string
}

const KanbanColumn = (props: KanbanColumnProps) => {
  const {
    actions,
    children,
    count,
    footer,
    icon,
    id,
    isActive,
    itemIds,
    locked,
    lockedTooltip,
    onAddItem,
    sx,
    title
  } = props

  const { setNodeRef } = useDroppable({ id })

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: isActive ? 'primary.main' : 'transparent',
        borderRadius: 2,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        minHeight: 400,
        minWidth: 0,
        p: 2,
        pt: 1,
        transition: 'border-color 0.2s ease',
        ...sx
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1
        }}
      >
        <SectionHeader
          actions={actions}
          icon={icon}
          sx={{
            flex: 1
          }}
          title={title}
          titleAdornment={
            count !== undefined ? (
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px',
                  px: 1,
                  py: 0.25
                }}
              >
                <Text
                  color="text.secondary"
                  sx={{ fontWeight: 700 }}
                  variant="caption"
                >
                  {count}
                </Text>
              </Box>
            ) : null
          }
        />
        {onAddItem ? (
          <IconButton onClick={onAddItem} size="small">
            <Icon fontSize="small" name="Add" />
          </IconButton>
        ) : locked ? (
          <Tooltip title={lockedTooltip}>
            <Box sx={{ display: 'inline-flex', p: 0.5 }}>
              <Icon color="disabled" fontSize="small" name="Lock" />
            </Box>
          </Tooltip>
        ) : null}
      </Box>

      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto'
        }}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
      </Box>

      {footer ? (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            mt: 'auto',
            pt: 1.5
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  )
}

export default KanbanColumn
